import React, { useState, useEffect, useCallback } from 'react';
import { connect, useDispatch } from 'react-redux';
import { formValueSelector } from 'redux-form';
import { Spin } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import PageHeader from '../layout/PageHeader';
import WalletCreateModal from '../components/WalletCreateModal/WalletCreateModal';
import ClientsWalletsFilters from '../components/WalletsPage/ClientsWalletsFilters';
import ClientsNewList from '../components/WalletsPage/ClientsNewList';
import Wallets2IntroModal from '../components/WalletsPage/Wallets2IntroModal';
import { identity } from '../utils/getIdentity';
import { getMerchantData } from '../redux/actions/merchantData';
import {
  getMerchantWallets,
  getMoreMerchantWallets,
  deleteMerchantWallet,
  resetTwoFaState,
  getWalletPrivateKey,
  getWalletMnemonicPhrase,
  getPassphrase,
  resetPageCounter,
  merchantWalletsReset,
} from '../redux/actions/merchantWallets';

const CLIENTS_NEW_HIDE_ZERO_STORAGE_KEY = 'cpayHideZeroBalancesClientsNew';
const FORM_NAME = 'clientsNewFilters';

const ClientsNewPage = ({
  merchantId,
  userRole,
  networkFilter,
  merchantData,
  currencies,
  wallets,
  page,
  pages,
  countItem,
  fetching,
  walletsFilters,
  getMerchantData,
  getMerchantWallets,
  getMoreMerchantWallets,
  deleteMerchantWallet,
  resetTwoFaState,
  getWalletPrivateKey,
  getWalletMnemonicPhrase,
  getPassphrase,
  resetPageCounter,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [isDisableSpinner, setIsDisableSpinner] = useState(false);
  const [showIntroModal, setShowIntroModal] = useState(false);
  const [walletVersionTick, setWalletVersionTick] = useState(0);
  const [hideZeroCheckbox, setHideZeroCheckbox] = useState(() => {
    try {
      const stored = localStorage.getItem(CLIENTS_NEW_HIDE_ZERO_STORAGE_KEY);
      return stored ? JSON.parse(stored) : false;
    } catch {
      return false;
    }
  });

  useEffect(() => {
    dispatch(merchantWalletsReset());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (merchantId) {
      getMerchantData(merchantId);
    }
    // getMerchantData is intentionally omitted — stable dispatch-bound function
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [merchantId]);

  useEffect(() => {
    if (merchantData?.settingWallet?.defaultSetup === 'v1' && merchantData?.settingWallet?.version === 'v1') {
      setShowIntroModal(true);
      setIsDisableSpinner(true);
    }
  }, [merchantData?.settingWallet?.defaultSetup, merchantData?.settingWallet?.version]);

  useEffect(() => {
    const bump = () => setWalletVersionTick(n => n + 1);
    const onStorage = e => {
      if (e.key === 'walletVersion') bump();
    };
    window.addEventListener('walletVersionChange', bump);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener('walletVersionChange', bump);
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  const currencyIds = currencies?.map(c => c._id) ?? [];
  const effectiveWalletVersion = merchantData?.settingWallet?.version || localStorage.getItem('walletVersion');

  const submitFilters = useCallback(() => {
    const { order, search, currencyIds: selectedIds } = walletsFilters || {};
    if (merchantId && networkFilter && currencyIds.length > 0) {
      resetPageCounter();
      getMerchantWallets(merchantId, {
        typeNetwork: networkFilter,
        walletVersion: 'v2',
        order: order || 'DESC',
        limit: 10,
        page: 1,
        currencyIds: (selectedIds?.length > 0 ? selectedIds : currencyIds).join(',') || undefined,
        typeWallet: 'user',
        showTokens: false,
        search: (search || '').trim() || undefined,
        positiveBalance: hideZeroCheckbox,
      });
    }
  }, [merchantId, networkFilter, currencyIds, walletsFilters, hideZeroCheckbox, resetPageCounter, getMerchantWallets]);

  // Initial fetch triggered when Redux Form initializes (walletsFilters.limit becomes 10)
  // and when sort/currency/hideZero changes
  useEffect(() => {
    if (
      walletsFilters &&
      typeof walletsFilters === 'object' &&
      merchantId &&
      merchantId.length &&
      currencyIds.length > 0
    ) {
      if (walletsFilters.limit || walletsFilters.order) {
        submitFilters();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [merchantId, walletsFilters?.limit, walletsFilters?.order, networkFilter, hideZeroCheckbox, walletVersionTick]);

  const resetFilters = useCallback(() => {
    if (merchantId && networkFilter && currencyIds.length > 0) {
      resetPageCounter();
      getMerchantWallets(merchantId, {
        typeNetwork: networkFilter,
        walletVersion: 'v2',
        order: 'DESC',
        limit: 10,
        page: 1,
        currencyIds: currencyIds.join(',') || undefined,
        typeWallet: 'user',
        showTokens: false,
        positiveBalance: hideZeroCheckbox,
      });
    }
  }, [merchantId, networkFilter, currencyIds, hideZeroCheckbox, resetPageCounter, getMerchantWallets]);

  const refreshWallets = useCallback(() => {
    const { order, search, currencyIds: selectedIds } = walletsFilters || {};
    if (merchantId && networkFilter && currencyIds.length > 0) {
      getMerchantWallets(merchantId, {
        typeNetwork: networkFilter,
        walletVersion: 'v2',
        order: order || 'DESC',
        limit: 10,
        page: page || 1,
        currencyIds: (selectedIds?.length > 0 ? selectedIds : currencyIds).join(',') || undefined,
        typeWallet: 'user',
        showTokens: false,
        search: (search || '').trim() || undefined,
        positiveBalance: hideZeroCheckbox,
      });
    }
  }, [merchantId, networkFilter, currencyIds, walletsFilters, hideZeroCheckbox, page, getMerchantWallets]);

  const WalletCreateComponent = () => (
    <WalletCreateModal
      merchantId={merchantId}
      typeWallet="user"
      createMode="type"
      filtersFormName={FORM_NAME}
      networkFilter={networkFilter}
    />
  );

  const showEmptyState = !fetching && (!wallets || wallets.length === 0);
  const showList = !showIntroModal && wallets?.length > 0;

  const handleSetCheckbox = useCallback(value => {
    setHideZeroCheckbox(value);
    localStorage.setItem(CLIENTS_NEW_HIDE_ZERO_STORAGE_KEY, JSON.stringify(value));
  }, []);

  return (
    <React.Fragment>
      <Wallets2IntroModal
        visible={showIntroModal}
        onClose={() => {
          setShowIntroModal(false);
          setIsDisableSpinner(false);
        }}
        merchantId={merchantId}
        networkFilter={networkFilter}
        currencyIds={currencyIds}
        getMerchantWallets={() => {
          setIsDisableSpinner(false);
          getMerchantWallets(merchantId, { typeNetwork: networkFilter });
        }}
        getMerchantData={getMerchantData}
        typeWallet="user"
        translationKey="clients.clients2Intro"
      />
      <PageHeader showBackButton={userRole.includes('corporate')} CreateModalComponent={WalletCreateComponent} />
      <div className="block-for-component">
        <div className={`component component-${identity}`}>
          <ClientsWalletsFilters
            formName={FORM_NAME}
            merchantName={merchantData?.name || ''}
            currencies={currencies}
            initialValues={{
              search: '',
              order: 'DESC',
              limit: 10,
              currencyIds: currencyIds,
            }}
            submitFilters={submitFilters}
            resetFilters={resetFilters}
            fetching={fetching}
            hideZeroCheckbox={hideZeroCheckbox}
            setCheckbox={handleSetCheckbox}
          />
          {!isDisableSpinner && fetching && !wallets?.length && (
            <div style={{ minHeight: 120, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <Spin indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} />
            </div>
          )}
          {showList && (
            <ClientsNewList
              wallets={wallets}
              page={page}
              pages={pages}
              fetching={fetching}
              merchantId={merchantId}
              networkFilter={networkFilter}
              currencyIds={currencyIds}
              getMoreMerchantWallets={getMoreMerchantWallets}
              search={walletsFilters?.search || ''}
              hideZeroCheckbox={hideZeroCheckbox}
              walletVersion={effectiveWalletVersion}
              deleteMerchantWallet={deleteMerchantWallet}
              getWalletPrivateKey={getWalletPrivateKey}
              getWalletMnemonicPhrase={getWalletMnemonicPhrase}
              getPassphrase={getPassphrase}
              resetTwoFaState={resetTwoFaState}
              countItem={countItem}
              walletsFilters={walletsFilters}
              refreshWallets={refreshWallets}
              typeWallet="user"
            />
          )}
          {showEmptyState && (
            <div className="wallets-new-empty">
              <p className="wallets-new-empty__text">{t('nodata')}</p>
            </div>
          )}
        </div>
      </div>
    </React.Fragment>
  );
};

const mapStateToProps = state => ({
  merchantId: state.transactions.merchantId,
  userRole: state.user.data.roles,
  networkFilter: state.networkFilter,
  merchantData: state.merchantData.data,
  currencies: state.currencies.data,
  wallets: state.merchantWallets.data,
  page: state.merchantWallets.page,
  pages: state.merchantWallets.pages,
  countItem: state.merchantWallets.countItem,
  fetching: state.merchantWallets.fetching,
  walletsFilters: formValueSelector(FORM_NAME)(state, 'search', 'order', 'limit', 'currencyIds'),
});

const mapDispatchToProps = dispatch => ({
  getMerchantData: getMerchantData(dispatch),
  getMerchantWallets: getMerchantWallets(dispatch),
  getMoreMerchantWallets: getMoreMerchantWallets(dispatch),
  deleteMerchantWallet: deleteMerchantWallet(dispatch),
  resetTwoFaState: resetTwoFaState(dispatch),
  getWalletPrivateKey: getWalletPrivateKey(dispatch),
  getWalletMnemonicPhrase: getWalletMnemonicPhrase(dispatch),
  getPassphrase: getPassphrase(dispatch),
  resetPageCounter,
});

export default connect(mapStateToProps, mapDispatchToProps)(ClientsNewPage);
